import React, { useMemo } from 'react';
import { Select, SelectOption, Theme } from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { BillingEventType, BillingProfile, SelectItem, TabsCustomer } from './billing-manager-types';

const useStyles = createUseStyles(
  {
    wrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      marginBottom: 20,
      '& h2': {
        margin: 0,
        fontSize: 18,
      },
    },
    headerRow: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      gap: 15,
    },
    select: {
      flexGrow: 1,
      border: `1px solid ${Theme.grey30}`,
    },
    hint: {
      fontSize: 10,
    },
  },
  { name: 'BillingManagerProfile' },
);

interface BillingProfileViewProps {
  profile: BillingProfile;
  sitesMap: Map<string, string>;
  customerList: TabsCustomer[];
  eventTypesList: BillingEventType[];
  orgSiteOptions: React.ReactNode;
  customerHubspotId?: string | null;
  changeCustomerAssignment: (selection: SelectItem[]) => void;
  changeOrgSiteConnections: (selection: SelectItem[]) => void;
  changeSaasEvent: (selection: SelectItem[]) => void;
  changeMMEvent: (selection: SelectItem[]) => void;
}

const BillingProfileView: React.FC<BillingProfileViewProps> = ({
  profile,
  sitesMap,
  customerList,
  eventTypesList,
  orgSiteOptions,
  customerHubspotId,
  changeCustomerAssignment,
  changeOrgSiteConnections,
  changeSaasEvent,
  changeMMEvent,
}: BillingProfileViewProps) => {
  const classes = useStyles();

  const selectedSellerSites: SelectItem[] = useMemo(() => {
    if (!profile?.orgSiteIds) return [];
    return profile.orgSiteIds.map((orgId) => ({
      text: sitesMap.get(orgId) || 'Unknown Site',
      value: orgId,
    }));
  }, [profile, sitesMap]);

  const customerOptions = useMemo(() => {
    return customerList.map((c) => (
      <SelectOption key={c.id} value={c.id}>
        {c.customerName} {c.hasContract ? '' : '(No Contract)'}
      </SelectOption>
    ));
  }, [customerList]);

  const selectedCustomers: SelectItem[] = useMemo(() => {
    if (!profile?.tabsCustomerId) return [];
    const customer = customerList.find((c) => c.id === profile.tabsCustomerId);
    if (!customer) return [];
    return [
      {
        text: `${customer.customerName} ${customer.hasContract ? '' : '(No Contract)'}`,
        value: profile.tabsCustomerId,
      },
    ];
  }, [profile, customerList]);

  const eventTypeOptions = useMemo(() => {
    return eventTypesList.map((ev) => (
      <SelectOption key={ev.id} value={ev.id}>
        {ev.name}
      </SelectOption>
    ));
  }, [eventTypesList]);

  const selectedSaasEvents: SelectItem[] = useMemo(() => {
    if (!profile?.saasEventId) return [];
    let eventName = 'SaaS Event';
    if (eventTypesList.length > 0) {
      const selectedEvent = eventTypesList.find((ev) => ev.id === profile.saasEventId);
      if (selectedEvent) eventName = selectedEvent.name;
      else eventName = 'Event Not Found!!';
    }
    return [
      {
        text: eventName,
        value: profile.saasEventId,
      },
    ];
  }, [profile, eventTypesList]);

  const selectedMMEvents: SelectItem[] = useMemo(() => {
    if (!profile?.mmEventId) return [];
    let eventName = 'MM Event';
    if (eventTypesList.length > 0) {
      const selectedEvent = eventTypesList.find((ev) => ev.id === profile.mmEventId);
      if (selectedEvent) eventName = selectedEvent.name;
      else eventName = 'Event Not Found!!';
    }
    return [
      {
        text: eventName,
        value: profile.mmEventId,
      },
    ];
  }, [profile, eventTypesList]);

  return (
    <div className={classes.wrap}>
      <div>
        <h2>TABS Customer</h2>
        <Select
          containerClassName={classes.select}
          search
          minimal
          placeholderText="Select a TABS Customer"
          onChange={changeCustomerAssignment}
          selectedItems={selectedCustomers}
        >
          {customerOptions}
        </Select>
        <div className={classes.hint}>
          If you can't find the customer here, they may have an existing profile connected to other sites.
        </div>
      </div>
      {selectedCustomers.length > 0 && (
        <div>
          View Customer in{' '}
          {customerHubspotId && (
            <>
              <a
                href={`https://app.hubspot.com/contacts/605444/company/${customerHubspotId}/`}
                target="_blank"
                rel="noopener"
              >
                Hubspot
              </a>{' '}
              or{' '}
            </>
          )}
          {selectedCustomers.length > 0 && (
            <a
              href={`https://app.tabsplatform.com/merchant/customers/${selectedCustomers[0].value}/overview`}
              target="_blank"
              rel="noopener"
            >
              TABS
            </a>
          )}
        </div>
      )}
      <div>
        <h2>Connected Seller Orgs</h2>
        <Select
          containerClassName={classes.select}
          search
          multiple
          minimal
          placeholderText="Connect Seller Orgs"
          onChange={changeOrgSiteConnections}
          selectedItems={selectedSellerSites}
        >
          {orgSiteOptions}
        </Select>
      </div>
      <div>
        <h2>SaaS/Enterprise Usage Event</h2>
        <Select
          containerClassName={classes.select}
          search
          minimal
          placeholderText="Select an Event Type"
          onChange={changeSaasEvent}
          selectedItems={selectedSaasEvents}
        >
          {eventTypeOptions}
        </Select>
      </div>
      <div>
        <h2>Managed Marketplace Usage Event</h2>
        <Select
          containerClassName={classes.select}
          search
          placeholderText="Select an Event Type"
          minimal
          onChange={changeMMEvent}
          selectedItems={selectedMMEvents}
        >
          {eventTypeOptions}
        </Select>
      </div>
    </div>
  );
};

export default BillingProfileView;
